import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";

// Imported assets
import { CheckmarkIcon, FilterIcon } from "../../../../../assets/icons/svgs/Icons";
import CloseIcon from "../../../../../assets/icons/close.svg";

// Imported components
import PrimaryButton from "../../../../../components/ui/PrimaryButton";
import TextButton from "../../../../../components/ui/TextButton";

// Styled components
const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
  margin-bottom: 10px;
  margin-left: 20px;
  width: 100%;

  & * {
    font-size: 12px;
  }
`;

const CloseButton = styled.img`
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 16px;
  height: auto;
  margin: 10px;
  position: absolute;
  right: 0px;
  top: 0px;
  width: 32px;
  z-index: 120;
`;

const DisplayingText = styled.div`
  align-self: flex-end;
  margin-left: auto;
  margin-right: 20px;
`;

const FilterButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 40px;
  padding: 0 20px;
  width: auto;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 10) a b / 100);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  flex-shrink: 0;
  position: relative;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  position: relative;
  padding: 2px;
`;

const FormRow = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: 20px;
  justify-content: space-between;
  margin-top: 20px;
  position: relative;
  width: 100%;

  &:first-child {
    margin-top: 40px;
  }
`;

const Icon = styled.img`
  height: auto;
  margin-left: 10px;
  width: 16px;
`;

const Modal = styled.div`
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: row;
  left: 50%;
  max-height: 80%;
  max-width: 80%;
  overflow-y: auto;
  padding: 20px;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  width: auto;
  z-index: 110;
`;

const OpenModalButton = styled.button`
  z-index: 5;
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;

  &:hover {
    background-color: #0056b3;
  }
`;

const Overlay = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 109;
`;

const FilterControls = ({ faultTypes, handleFilterChange, locationTypes }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedFaultTypes, setSelectedFaultTypes] = useState([]);
  const [selectedLocationTypes, setSelectedLocationTypes] = useState([]);

  // simply toggle state of the modal
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSelectAllFaultTypes = useCallback((e) => {
    e?.stopPropagation();

    let newFaultTypes = [];
    for (let i = 0; i < faultTypes.length; i++) {
      newFaultTypes.push(faultTypes[i].codeName);
    }
    setSelectedFaultTypes(newFaultTypes);
  }, []);

  const handleSelectAllLocTypes = useCallback((e) => {
    e?.stopPropagation();

    let newLocTypes = [];
    for (let i = 0; i < locationTypes.length; i++) {
      newLocTypes.push(locationTypes[i].codeName);
    }
    setSelectedLocationTypes(newLocTypes);
  }, []);

  // Initialize filters state from props or default values
  useEffect(() => {
    handleSelectAllFaultTypes();
    handleSelectAllLocTypes();
  }, [handleSelectAllFaultTypes, handleSelectAllLocTypes]);

  // Send changes to filters on change
  useEffect(() => {
    handleFilterChange({
      faultTypes: selectedFaultTypes,
      locationTypes: selectedLocationTypes,
    });
  }, [selectedFaultTypes, selectedLocationTypes]);

  return (
    <div>
      <FilterButton onClick={toggleModal}>
        Filter
        <FilterIcon fill="white" />
      </FilterButton>

      <Overlay $isOpen={isModalOpen} onClick={toggleModal} />

      <Modal $isOpen={isModalOpen} id="modal">
        <CloseButton onClick={toggleModal} src={CloseIcon} />

        <Form>
          <FormRow>
            <FormField>
              <ButtonGroup>
                <TextButton
                  onClick={() => handleSelectAllFaultTypes()}
                  type="button"
                >
                  Select All
                </TextButton>

                <TextButton
                  onClick={() => setSelectedFaultTypes([])}
                  type="button"
                >
                  Clear
                </TextButton>

                <DisplayingText>
                  Displaying {selectedFaultTypes.length}
                </DisplayingText>
              </ButtonGroup>

              <FormControl sx={{ m: 1, width: 400 }}>
                <Autocomplete
                  disableCloseOnSelect
                  getOptionLabel={(option) => option}
                  id="fault-types-autocomplete"
                  limitTags={1}
                  multiple
                  noOptionsText="No results found"
                  onChange={(e, newValue) => {
                    setSelectedFaultTypes(newValue);
                  }}
                  options={faultTypes.map((t) => t.codeName)}
                  renderInput={(params) => {
                    const { InputProps, ...restParams } = params;
                    const { startAdornment, ...restInputProps } = InputProps;
                    return (
                      <TextField
                        {...restParams}
                        label="Fault Types"
                        placeholder="Search"
                        slotProps={{
                          input: {
                            ...restInputProps,
                            startAdornment: (
                              <div
                                style={{
                                  maxHeight: 38,
                                  overflowY: "auto",
                                }}
                              >
                                {startAdornment}
                              </div>
                            ),
                          },
                        }}
                      />
                    );
                  }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <MenuItem key={key} sx={{ mt: "4px" }} {...optionProps}>
                        {selected && <CheckmarkIcon fill="white" />}
                        {option}
                      </MenuItem>
                    );
                  }}
                  value={selectedFaultTypes}
                />
              </FormControl>
            </FormField>

            <FormField>
              <ButtonGroup>
                <TextButton
                  onClick={() => handleSelectAllLocTypes()}
                  type="button"
                >
                  Select All
                </TextButton>

                <TextButton
                  onClick={() => setSelectedLocationTypes([])}
                  type="button"
                >
                  Clear
                </TextButton>

                <DisplayingText>
                  Displaying {selectedLocationTypes.length}
                </DisplayingText>
              </ButtonGroup>

              <FormControl sx={{ m: 1, width: 400 }}>
                <Autocomplete
                  disableCloseOnSelect
                  getOptionLabel={(option) => option}
                  id="location-types-autocomplete"
                  limitTags={1}
                  multiple
                  noOptionsText="No results found"
                  onChange={(e, newValue) => {
                    setSelectedLocationTypes(newValue);
                  }}
                  options={locationTypes.map((t) => t.codeName)}
                  renderInput={(params) => {
                    const { InputProps, ...restParams } = params;
                    const { startAdornment, ...restInputProps } = InputProps;
                    return (
                      <TextField
                        {...restParams}
                        label="Location Types"
                        placeholder="Search"
                        slotProps={{
                          input: {
                            ...restInputProps,
                            startAdornment: (
                              <div
                                style={{
                                  maxHeight: 38,
                                  overflowY: "auto",
                                }}
                              >
                                {startAdornment}
                              </div>
                            ),
                          },
                        }}
                      />
                    );
                  }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <MenuItem key={key} sx={{ mt: "4px" }} {...optionProps}>
                        {selected && <CheckmarkIcon fill="white" />}
                        {option}
                      </MenuItem>
                    );
                  }}
                  value={selectedLocationTypes}
                />
              </FormControl>
            </FormField>
          </FormRow>
        </Form>
      </Modal>
    </div>
  );
};

export default FilterControls;
